/* ================================================================================================
 * <hhds-intersection-observer> — replaces WebAwesome's <wa-intersection-observer>.
 *
 * Wrap content to receive `hhds-intersect` events when the wrapper enters / leaves the viewport.
 * Mirrors the API the project depends on:
 *   - attribute `root-margin="<top> <right>"` (default `"0px 0px"`)
 *   - attribute `threshold="0, 0.1, 0.5"` (comma-separated, default `0`)
 *   - attribute `once` — boolean, disconnect after first intersection
 *   - attribute `intersect-class="hhds-revealed"` — class added to the host on intersection
 *
 * Emits a `hhds-intersect` CustomEvent with `{ entry: IntersectionObserverEntry }` in `detail`.
 * The event is non-bubbling to match the consumer's `addEventListener` behaviour.
 * ================================================================================================ */

const TAG_NAME = 'hhds-intersection-observer';

class HhdsIntersectionObserver extends HTMLElement {
	private observer: IntersectionObserver | null = null;
	private hasIntersected = false;

	static get observedAttributes() {
		return ['root-margin', 'threshold', 'intersect-class', 'once'];
	}

	connectedCallback() {
		this.connectObserver();
	}

	disconnectedCallback() {
		this.observer?.disconnect();
		this.observer = null;
	}

	attributeChangedCallback() {
		if (this.observer) {
			this.observer.disconnect();
			this.connectObserver();
		}
	}

	private connectObserver() {
		const rootMargin = this.getAttribute('root-margin') ?? '0px 0px';
		const thresholdAttr = this.getAttribute('threshold') ?? '0';
		const threshold = thresholdAttr
			.split(',')
			.map((v) => parseFloat(v.trim()))
			.filter((v) => !Number.isNaN(v));

		this.observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						const intersectClass = this.getAttribute('intersect-class');
						if (intersectClass) {
							this.classList.add(intersectClass);

							const firstChild = this.firstElementChild as HTMLElement | null;
							if (firstChild && firstChild !== this) {
								firstChild.classList.add(intersectClass);
							}
						}
						this.hasIntersected = true;
					}

					this.dispatchEvent(new CustomEvent('hhds-intersect', { detail: { entry } }));

					if (this.hasIntersected && this.hasAttribute('once')) {
						this.observer?.disconnect();
						this.observer = null;
						break;
					}
				}
			},
			{ rootMargin, threshold: threshold.length ? threshold : 0 }
		);

		this.observer.observe(this);
	}
}

if (!customElements.get(TAG_NAME)) {
	customElements.define(TAG_NAME, HhdsIntersectionObserver);
}

export { HhdsIntersectionObserver };
